import get from 'lodash.get';
import cloneDeep from 'lodash.clonedeep';
import clone from 'lodash.clone';
import map from 'lodash.map';
import { store } from '@/appContext';
import EasyPassClient from './EasyPassClient';
import AuctionHouseClient from './AuctionHouseClient';
import OffersAndNegotiationClient from './OffersAndNegotiationClient';
import { isUndefinedOrNullOrEmpty } from '../utilities';

function setAuctionType(response) {
  const clonedResponse = cloneDeep(response);
  const auction = clonedResponse.data;
  const sellerDealerId = get(auction, 'seller_info.dealer_id');
  const participant = get(auction, 'participant');
  const isMemberOfSellingDealership = store.getters
    .currentUser.isMemberOfSellingDealership(sellerDealerId);

  if (participant) {
    auction.type = 'buying';
  }

  if (isMemberOfSellingDealership) {
    auction.type = 'selling';
  }
  return clonedResponse;
}

class AuctionClient {
  constructor(user) {
    this.easyPassClient = new EasyPassClient(user);
    this.auctionHouseClient = new AuctionHouseClient(user);
    this.offersAndNegotiationClient = new OffersAndNegotiationClient();
    this.rootUri = 'auction';
    this.apiVersion = 'v2';
    this.easyPassRootUri = 'api/acvapi/v3';
    this.easyPassHeaders = {
      'json-naming-strategy': 'camel',
    };
  }

  async get(auctionId) {
    if (!auctionId || Number.isNaN(auctionId)) {
      store.dispatch('analytics/noticeError', {
        error: new Error(`AuctionClient.get called with invalid auctionId: ${auctionId}`),
        customAttributes: { auctionId, error_category: 'invalid_auction_id' },
      });
      return null;
    }
    const params = null;
    const response = await this.easyPassClient.get(`bff/auctions/buying/search/auction/${auctionId}`, params, {
      'json-naming-strategy': 'snake',
    });

    return setAuctionType(response.data);
  }

  getStatus(auctionId) {
    return this.getStatuses([auctionId])
      .then((auctions) => auctions[0]);
  }

  getStatuses(auctionIds) {
    return this.easyPassClient.post(`${this.easyPassRootUri}/${this.rootUri}/statuses`, { auctions: auctionIds })
      .then((response) => map(response.data.data.auctions, (auction, auctionId) => {
        const updatedAuction = clone(auction);
        updatedAuction.id = parseInt(auctionId, 10);
        return updatedAuction;
      }));
  }

  getNotificationData(auctionId) {
    return this.easyPassClient.get(
      `${this.easyPassRootUri}/${this.rootUri}/${auctionId}/notification_info`,
    )
      .then((response) => response.data);
  }

  submitBid(auctionId, amount, userId, proxy = false, persistent = false) {
    return this.auctionHouseClient.submitBid(auctionId, amount, proxy, persistent);
  }

  async getBillOfSales(auctionId) {
    const response = await this.easyPassClient.downloadFile(`${this.easyPassRootUri}/${this.rootUri}/${auctionId}/documents/bill_of_sale`);
    const contentType = response.headers['content-type'];
    const blob = new Blob([response.data], { type: contentType });
    const filename = `Billofsale-${auctionId}`;
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
    } else {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.dispatchEvent(new MouseEvent('click'));
    }
  }

  async getProxyBid(auctionId) {
    const useAHEndpoint = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.proxyBidInfoUseAuctionHouseEndpointWeb,
    );

    if (useAHEndpoint) {
      return this.auctionHouseClient.getProxyBidInfo(auctionId)
        .then((ahResponse) => ({
          data: {
            proxy: ahResponse.amount,
            is_persistent: ahResponse.is_persistent,
          },
        }));
    }

    return this.easyPassClient.get(`${this.easyPassRootUri}/${this.rootUri}/${auctionId}/proxy_bid_info`)
      .then((response) => response.data);
  }

  async getPersistentProxyForAuction(auctionId) {
    const response = await this.easyPassClient.get(
      `${this.easyPassRootUri}/myacv/auctions/buying/persistent_proxy`,
      { query: auctionId, size: 12, from: 0 },
    );
    const auctions = get(response, 'data.data.auctions', []);
    const auction = auctions.find((a) => a.id === auctionId);
    const proxyBid = get(auction, 'proxy_bid', null);
    return {
      proxy: proxyBid ? proxyBid.amount : null,
      is_persistent: get(proxyBid, 'is_persistent', false),
    };
  }

  getCheckoutOptions(
    auctionId,
    dealerId,
    include = [],
    cancelToken = null,
    paymentMthd = null,
    shouldUseOmsItemizedBuyFees = false,
  ) {
    /**
     * Include response properties via keywords of:
     * arbitration, transportation, payment, post, capital
    */
    const inclusionQuery = {
      include: include.join(','),
    };
    if (shouldUseOmsItemizedBuyFees && paymentMthd) {
      inclusionQuery.paymentMethod = paymentMthd;
    }
    return this.easyPassClient.get(`bff/orders/${auctionId}/checkout_options/${dealerId}`, inclusionQuery, null, cancelToken)
      .then((response) => response.data.data);
  }

  setBuyingDealership(auctionId, dealershipId) {
    return this.easyPassClient.post(`${this.easyPassRootUri}/${this.rootUri}/${auctionId}/buyer/${dealershipId}`);
  }

  fetchFinalizingDealerships(auctionId) {
    return this.easyPassClient.post(`${this.easyPassRootUri}/${this.rootUri}/${auctionId}/buyer/valid_dealerships`)
      .then((response) => response.data.data);
  }

  finalizeAuction(
    auctionId,
    dealershipId,
    transportAccepted,
    paymentMethod,
    arbitrationPlanId,
    deliveryAddressId,
    transportServiceType,
  ) {
    const data = {
      buyer_organization_id: dealershipId,
      transport_quote_accepted: transportAccepted,
      payment_provider_name: paymentMethod,
      arbitration_plan_id: arbitrationPlanId,
      delivery_address_id: deliveryAddressId,
      transportation_service_type: transportServiceType,
    };

    return this.easyPassClient
      .put(
        `bff/orders/finalize/${auctionId}`,
        data,
      )
      .then((response) => response.data);
  }

  updatePersisentProxy(auctionId, userId, amount) {
    const data = {
      amount,
      user_id: userId,
    };

    return this.easyPassClient.put(`${this.easyPassRootUri}/${this.rootUri}/${auctionId}/persistent_proxy_bid`, data)
      .then((response) => response.data);
  }

  createPersistentProxy(auctionId, userId, amount) {
    return this.auctionHouseClient.post(
      `external/v1/auctions/${auctionId}/persistent_proxy_bid`,
      { amount, user_id: userId },
    ).then((response) => response.data);
  }

  cancelPersisentProxy(auctionId, userId) {
    const data = {
      user_id: userId,
    };
    return this.easyPassClient.delete(`${this.easyPassRootUri}/${this.rootUri}/${auctionId}/persistent_proxy_bid`, data)
      .then((response) => response.data);
  }

  getReportAProblemOptions(auctionId, dealerId) {
    return this.easyPassClient.get(`bff/arbitrations/actions/${auctionId}`, { dealer_id: dealerId })
      .then((response) => {
        // We need to set a default value
        // based on whether they have marked as abandoned or not. -ETC
        let data = get(response, 'data.data');
        data = data.map((option) => {
          const modifiedOption = cloneDeep(option);
          if (modifiedOption.input_type.toLowerCase() === 'toggle') {
            modifiedOption.value = !isUndefinedOrNullOrEmpty(modifiedOption.issue);
          }
          return modifiedOption;
        });

        if (response.data) {
          response.data.data = data;
        } else {
          response.data = {
            data,
          };
        }

        return response;
      });
  }

  submitProblem(auctionId, actionType, value, dealerId) {
    const data = {
      type: actionType,
      value: value.toString(),
      dealer_id: dealerId,
    };

    return this.easyPassClient.post(`${this.easyPassRootUri}/${this.rootUri}/${auctionId}/issues`, data);
  }

  getCarfaxReport(vin, auctionId, dealerId) {
    return this.easyPassClient.get(`api/acvapi/v1/vehicle/${vin}/carfax`, {
      auction_id: auctionId,
      dealer_id: dealerId,
    });
  }

  makeOffer(auctionId, amount, dealerId, useCamelNamingStrategy = true) {
    const data = {
      amount,
      dealer_id: dealerId,
    };

    return this.easyPassClient.post(
      `bff/auction/${auctionId}/offers`,
      data,
      useCamelNamingStrategy ? this.easyPassHeaders : null,
      null,
      6,
    );
  }

  incrementAuctionViews(auctionId) {
    return this.easyPassClient.post(`${this.easyPassRootUri}/auction/${auctionId}/view_count`);
  }

  getPostAuctionInfo(auctionId) {
    return this.easyPassClient.get(`${this.easyPassRootUri}/${this.rootUri}/${auctionId}/post_auction_info`);
  }

  getSellerDetails(dealerId) {
    return this.easyPassClient.get(`${this.easyPassRootUri}/dealership/${dealerId}/performance_info`);
  }

  getHighestRejectedOffer(auctionId) {
    return this.offersAndNegotiationClient.getHighestRejectedOffer(auctionId);
  }

  /**
   * Fetches highest rejected offers for a list of auction IDs
   * @param {Array<number>} auctionIds - Array of auction IDs or saved auction IDs
   * @param {boolean} useSavedAuctionIds - If true, uses saved_auction_ids param
   *   instead of auction_ids
   * @returns {Promise<Object>} API response containing restriction_offers data
   */
  async getHighestRejectedOffers(auctionIds, useSavedAuctionIds = false) {
    const response = await this.offersAndNegotiationClient.getHighestRejectedOffers(
      auctionIds,
      useSavedAuctionIds,
    );
    return response;
  }

  /**
   * Fetches one or more attributes (e.g. highest_rejected_offer_amount,
   * ended_auction_purchase_amount) for a list of auction IDs in a single call
   * @param {Array<string>} attributes - Attribute names to request
   * @param {Array<number>} auctionIds - Array of auction IDs or saved auction IDs
   * @param {boolean} useSavedAuctionIds - If true, uses saved_auction_ids param
   *   instead of auction_ids
   * @returns {Promise<Object>} API response containing results data
   */
  async getAttributes(attributes, auctionIds, useSavedAuctionIds = false) {
    const response = await this.offersAndNegotiationClient.getAttributes(
      attributes,
      auctionIds,
      useSavedAuctionIds,
    );
    return response;
  }

  /**
   * Deletes the highest rejected offer for an auction
   * @param {number} auctionId - The auction ID or saved auction ID
   * @param {boolean} isSavedAuction - If true, adds saved_auction_id=true query param
   * @returns {Promise<Object>} API response
   */
  async deleteHighestRejectedOffer(auctionId, isSavedAuction = false) {
    const response = await this.offersAndNegotiationClient.deleteHighestRejectedOffer(
      auctionId,
      isSavedAuction,
    );
    return response;
  }

  /**
   * Creates a Buy Now ("ended auction purchase") price for an ended auction
   * @param {number} auctionId - The auction ID
   * @param {number} amount - The Buy Now price amount
   * @returns {Promise<Object>} API response containing auction_id and amount
   */
  async createEndedAuctionPurchaseAmount(auctionId, amount) {
    const response = await this.offersAndNegotiationClient.createEndedAuctionPurchaseAmount(
      auctionId,
      amount,
    );
    return response;
  }

  /**
   * Accepts the Buy Now ("ended auction purchase") price for an auction
   * @param {number} auctionId - The auction ID
   * @param {number} amount - The Buy Now price amount
   * @returns {Promise<Object>} API response containing auction_id, deal_id, and amount
   */
  async acceptEndedAuctionPurchase(auctionId, amount) {
    const response = await this.offersAndNegotiationClient.acceptEndedAuctionPurchase(
      auctionId,
      amount,
    );
    return response;
  }

  /**
   * Deletes the Buy Now ("ended auction purchase") price for an auction
   * @param {number} auctionId - The auction ID or saved auction ID
   * @param {boolean} isSavedAuction - If true, calls the dedicated saved_auctions endpoint
   * @returns {Promise<Object>} API response
   */
  async deleteEndedAuctionPurchaseAmount(auctionId, isSavedAuction = false) {
    const response = await this.offersAndNegotiationClient.deleteEndedAuctionPurchaseAmount(
      auctionId,
      isSavedAuction,
    );
    return response;
  }
}

export default AuctionClient;
